import { MISSING_DATA_COLOR, RecoverableEnergyColors } from './lithium-color-helpers';

export interface EnergyProgressBarData {
  progressAvailableEnergy: number | null;
  progressRecoverableEnergy: number | null;
  progressEnergyLostToAging: number | null;
  progressEnergyMissingData: number | null;
}

export class EnergyProgressBar {
  data: EnergyProgressBarData;

  constructor(data: EnergyProgressBarData, isNormalized: boolean = true) {
    this.data = isNormalized
      ? data
      : this.normalizeToHundred({
          availableEnergy: data.progressAvailableEnergy,
          recoverableEnergy: data.progressRecoverableEnergy,
          energyMissingData: data.progressEnergyMissingData,
          energyLostToAging: data.progressEnergyLostToAging,
        });
  }

  get strokeColor(): Record<string, string> | null {
    if (
      !this.data.progressEnergyMissingData &&
      !this.data.progressAvailableEnergy &&
      !this.data.progressRecoverableEnergy &&
      !this.data.progressEnergyLostToAging
    )
      return null;

    let offset = 0;
    const strokeColor: Record<string, string> = {};
    for (const { color, value } of [
      {
        color: RecoverableEnergyColors.available,
        value: this.data.progressAvailableEnergy,
      },
      {
        color: RecoverableEnergyColors.recoverable,
        value: this.data.progressRecoverableEnergy,
      },
      {
        color: MISSING_DATA_COLOR,
        value: this.data.progressEnergyMissingData,
      },
      {
        color: RecoverableEnergyColors.aging,
        value: this.data.progressEnergyLostToAging,
      },
    ]) {
      offset = this.addColor(strokeColor, color, offset, value);
    }

    return strokeColor;
  }

  private addColor(strokeColor: Record<string, string>, color: string, offset: number, value: number | null) {
    const minValueToShow = 1e-5;

    if (!value || value <= minValueToShow) return offset;

    strokeColor[this.getColorKey(offset)] = color;
    offset += value - minValueToShow;
    strokeColor[this.getColorKey(offset)] = color;
    offset += minValueToShow;

    return offset;
  }

  private getColorKey(offset: number): string {
    return `${Math.min(offset, 100)}%`;
  }

  private normalizeToHundred({
    availableEnergy,
    recoverableEnergy,
    energyLostToAging,
    energyMissingData,
  }: {
    availableEnergy: number | null;
    recoverableEnergy: number | null;
    energyLostToAging: number | null;
    energyMissingData: number | null;
  }): EnergyProgressBarData {
    if (!availableEnergy && !recoverableEnergy && !energyLostToAging && !energyMissingData)
      return {
        progressAvailableEnergy: null,
        progressRecoverableEnergy: null,
        progressEnergyLostToAging: null,
        progressEnergyMissingData: null,
      };

    const total = (availableEnergy ?? 0) + (recoverableEnergy ?? 0) + (energyLostToAging ?? 0) + (energyMissingData ?? 0);
    const normalizedAvailableEnergy = ((availableEnergy ?? 0) / total) * 100;
    const normalizedRecoverableEnergy = ((recoverableEnergy ?? 0) / total) * 100;
    const normalizedEnergyLostToAging = ((energyLostToAging ?? 0) / total) * 100;
    const normalizedEnergyMissingData = ((energyMissingData ?? 0) / total) * 100;

    return {
      progressAvailableEnergy: normalizedAvailableEnergy,
      progressRecoverableEnergy: normalizedRecoverableEnergy,
      progressEnergyLostToAging: normalizedEnergyLostToAging,
      progressEnergyMissingData: normalizedEnergyMissingData,
    };
  }
}
